import { Draft } from 'immer'
import { Get } from 'type-fest'
import { OpalApiBFFComponent } from '@opallabs/api'
import { PlanResponse } from '../types'

type CategorySettings = OpalApiBFFComponent<'timeline_category'>['settings']
type GridSettings = Extract<
  NonNullable<CategorySettings>,
  { row_size: unknown }
>

export type CategoryAttributes = {
  blockTemplate?: { id: string; title: string } | null
  categoryType?: null | { id: string; name: string }
  position?: number
  size?: Get<GridSettings, 'row_size'>
  viewMode?: Get<GridSettings, 'view_mode'>
  title?: string | null
}

/** Used to fill in category.settings when we have an incomplete object to work with */
const DEFAULT_SETTINGS = {
  type: null,
  template_id: null,
  row_size: 'small',
  view_mode: 'default',
} satisfies CategorySettings

export const updateCategory = (
  categoryId: string,
  updateAttrs: CategoryAttributes
) => {
  return (draftResponse: Draft<PlanResponse>) => {
    const category = draftResponse.data.categories.by_id[categoryId]

    if (!category) {
      return draftResponse
    }

    if (updateAttrs.title !== undefined) {
      category.title = updateAttrs.title
    }

    if (updateAttrs.position !== undefined) {
      category.position = updateAttrs.position
    }

    // Ensure settings exist before any settings-bound write below.
    if (!category.settings) {
      category.settings = { ...DEFAULT_SETTINGS }
    }

    // Smart swimlane `settings` is read-only, so narrow to the block grid part of the type union
    const { settings } = category
    if ('row_size' in settings) {
      if (updateAttrs.blockTemplate !== undefined) {
        settings.template_id = updateAttrs.blockTemplate?.id ?? null
      }

      if (updateAttrs.categoryType !== undefined) {
        settings.type =
          updateAttrs.categoryType === null
            ? null
            : {
                id: updateAttrs.categoryType.id,
                name: updateAttrs.categoryType.name,
                description: null,
                deactivated_at: null,
                supported_paired_type: null,
              }
      }

      if (updateAttrs.size !== undefined) {
        settings.row_size = updateAttrs.size
      }

      if (updateAttrs.viewMode !== undefined) {
        settings.view_mode = updateAttrs.viewMode
      }
    }

    return draftResponse
  }
}
